import * as React from 'react';
import styled from 'styled-components';
import Headline from '@adecco/base-app/src/components/atoms/Headline/Headline';
import { moreThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import calcrem from '@adecco/base-app/src/utilities/calcrem/calcrem';
import { CloudinaryImageType } from '@adecco/base-app/src/gql/schema/generated';
import DynamicImage from '@adecco/base-app/src/components/atoms/DynamicImage/DynamicImage';
import { useAppContext } from '@adecco/base-app/src/utilities/appProvider/appProvider';

export interface IFact {
  title: string;
  description: string;
  icon?: string;
  cloudinaryImage?: CloudinaryImageType;
}

const FactStyled = styled.div<{ brand: string }>`
  border-radius: ${(props) => props.theme.radius.panel};
  box-shadow: ${(props) => props.theme.boxShadow.fact};
  padding: ${(props) => props.theme.paddings.fact};
  background-color: ${(props) => props.theme.backgrounds.fact};
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => props.theme.aligns.fact};
  ${(props) =>
    props.brand === 'Dis' &&
    `
    text-align: center;
  `}
  ${moreThan('md')} {
    padding: ${(props) => props.theme.paddings.factWideScreen};
    min-height: calc(${calcrem(32)} + ${calcrem(36)} + ${calcrem(204)});
  }
`;

const FactHeader = styled.div<{ brand: string }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: ${calcrem(20)};
  box-shadow: ${(props) => props.theme.boxShadow.factHeader};
  padding: ${(props) => props.theme.paddings.factHeader};
  border-radius: ${(props) => props.theme.radius.factHeader};
  ${(props) =>
    props.brand === 'Dis' &&
    `
    justify-content: center;
  `}
`;

const FactHeadline = styled.h4`
  font-family: ${(props) => props.theme.fonts.Headline};
  font-size: ${(props) => props.theme.fontSizes.factHeadline};
  line-height: ${(props) => props.theme.lineHeights.fact};
  margin-right: ${(props) => props.theme.margins.fact};
  color: ${(props) => props.theme.colors.fact};
  hyphens: auto;
  word-wrap: break-word;
`;

const Image = styled(DynamicImage)`
  display: ${(props) => props.theme.displays.factImage};
  width: auto;
  height: 100%;
  max-width: 100px;
  width: ${(props) => props.theme.widths.factImage};
  aspect-ratio: 1;
  object-fit: contain;
`;

const FactSubline = styled(Headline)`
  font-size: ${(props) => props.theme.fontSizes.factSubline};
  line-height: ${(props) => props.theme.lineHeights.factSubline};
  margin-bottom: 0;
  font-family: ${(props) => props.theme.fonts.factSubline};
  color: ${(props) => props.theme.colors.fact};
`;

const Fact: React.FunctionComponent<IFact> = ({ title, description, icon, cloudinaryImage }) => {
  const { brand } = useAppContext();
  return (
    <FactStyled brand={brand}>
      <FactHeader brand={brand}>
        <FactHeadline>{title}</FactHeadline>
        {brand !== 'Dis' && <Image cloudinaryImage={cloudinaryImage} image={icon} />}
      </FactHeader>
      <FactSubline skin={4} tag="h5">
        {description}
      </FactSubline>
    </FactStyled>
  );
};

export default Fact;
